/**
 * @file Application-wide configuration for the Angular Productivity Portal.
 *
 * Declares and wires up:
 * - Global providers (routing, HTTP client, SVG icon support, MSAL authentication, Kendo notifications).
 * - Internationalization (ngx-translate) including a custom loader and bootstrap-time language initialization.
 * - HTTP interceptors (error handling, employee impersonation).
 * - A custom global error handler.
 * - Zone coalescing optimization to reduce change detection frequency.
 * - Notification container attachment to the document body.
 *
 * Exported symbols documented below:
 * - createTranslateLoader
 * - appInitializerFactory
 * - appConfig
 */

/**
 * Creates the translation file loader used by ngx-translate.
 *
 * Loads JSON resource bundles from the assets/i18n folder using HttpClient.
 *
 * @param http HttpClient used to perform HTTP GET requests for translation JSON files.
 * @returns An instance of TranslateHttpLoader configured with path './assets/i18n/' and extension '.json'.
 *
 * @remarks
 * This factory is referenced in the TranslateModule.forRoot configuration so that the
 * translation service can lazily resolve localized resource files as languages are requested.
 */

/**
 * Produces an APP_INITIALIZER factory that sets up the default and active language for the application.
 *
 * @param translate The TranslateService instance injected at bootstrap time.
 * @returns A zero-argument function returning a promise/observable-like value produced by translate.use('en'),
 *          ensuring English resources are loaded before the application finishes initialization.
 *
 * @remarks
 * - Sets 'en' as both the default and active language.
 * - By returning the inner call, Angular waits for translation assets to load before rendering.
 * - Extend this initializer if you later introduce user preference or browser language negotiation logic.
 */

/**
 * Root ApplicationConfig object defining global providers for the standalone Angular application.
 *
 * Included provider groups:
 * - Change Detection: Enables event coalescing to reduce redundant CD cycles.
 * - Routing: Registers application routes via provideRouter(routes).
 * - HTTP: Registers HttpClient plus DI-sourced interceptors (error and impersonation).
 * - Internationalization: Configures TranslateModule with a custom loader and APP_INITIALIZER to preload 'en'.
 * - Authentication: Adds MSAL-based auth providers via provideAuth().
 * - SVG Icons: Enables angular-svg-icon integration.
 * - Notifications: Sets up Kendo NotificationModule and assigns document.body as the host container.
 * - Animations: Imports BrowserAnimationsModule (required for certain UI components / notifications).
 * - Error Handling: Replaces the default ErrorHandler with CustomErrorHandler for centralized logging / UX.
 * - Interceptors:
 *    * HttpErrorInterceptor: Captures and processes HTTP errors globally.
 *    * ImpersonateEmployeeInterceptor: Injects impersonation headers / context into outgoing requests.
 *
 * Design considerations:
 * - Order of HTTP_INTERCEPTORS matters (declared sequence reflects interception chain).
 * - provideHttpClient() is declared twice: once baseline, once with withInterceptorsFromDi() to ensure DI-registered interceptors are honored.
 * - NOTIFICATION_CONTAINER uses a factory to guarantee availability of document.body (avoiding direct template anchors).
 *
 * Extension guidelines:
 * - Add future feature-level providers here or in feature-specific environment modules to keep bootstrap lean.
 * - For multi-language support, extend appInitializerFactory to detect persisted or browser locale before calling translate.use(...).
 * - Consider extracting grouped provider arrays (e.g. httpProviders, i18nProviders) for test reusability.
 */
import {
  ApplicationConfig,
  ElementRef,
  ErrorHandler,
  importProvidersFrom,
  //inject,
  provideZoneChangeDetection,
} from '@angular/core';
//import { APP_CONFIG } from './app.tokens';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';

import {
  NOTIFICATION_CONTAINER,
  NotificationModule,
} from '@progress/kendo-angular-notification';
import { HttpErrorInterceptor } from './shared/interceptors/errors.interceptor';
import { CustomErrorHandler } from './shared/services/custom.error.handler.service';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideAuth } from './msal-config.factory';
import { ImpersonateEmployeeInterceptor } from './shared/interceptors/impersonate-employee.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(),
    provideAngularSvgIcon(),
    provideAnimations(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
    provideAuth(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ImpersonateEmployeeInterceptor,
      multi: true,
    },
    importProvidersFrom(NotificationModule),

    {
      provide: NOTIFICATION_CONTAINER,
      useFactory: () => {
        return { nativeElement: document.body } as ElementRef;
      },
    },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
  ],
};
